import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { maxLookbackDaysForTimeGranularity } from "@features/sales-v3/utils/max-lookback-days-for-time-granularity";
import { dateRangeOptionsForTimeGranularity } from "@north-beam/nb-common";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";

import { useSalesPageV3NextContext } from "../sales-v3-next-provider";

export const DATE_RANGE_CHOICE_LABELS = {
  today: "Today",
  yesterday: "Yest",
  yesterdayAndToday: "Yest & today",
  todayAndYesterday: "Today & yest",
  last3Days: "3d",
  last7Days: "7d",
  last14Days: "14d",
  last30Days: "30d",
  last60Days: "60d",
  monthToDate: "Mo to date",
  lastMonth: "Last mo",
  thisWeek: "This week",
  lastWeek: "Last week",
  thisWeekAndLastWeek: "This week & last week",
  last4Weeks: "1m",
  last12Weeks: "3m",
  last26Weeks: "6m",
  last52Weeks: "1y",
  yearToDate: "Year to date",
  thisMonth: "This mo",
  thisMonthAndLastMonth: "This mo & last mo",
  last3Months: "3m",
  last6Months: "6m",
  last9Months: "9m",
  last12Months: "12m",
  lastYear: "1yr",
  last3Years: "3yr",
  last5Years: "5yr",
} as const;

type Props = {
  className?: string;
};

export const DateFilters = ({ className }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRange,
    dateRangeInterval,
    compareDateRange,
    breakdowns,
    filters,
    setDateRange,
  } = useSalesPageV3NextContext();

  const [maxLookbackDays] = maxLookbackDaysForTimeGranularity(timeGranularity);

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
  };

  const [selected, setSelected] = useState<FlatSelectOption<string>>({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (typeof dateRange !== "string") {
      return setSelected({
        label: "",
        value: "custom",
      });
    }
    setSelected({
      label: "",
      value: dateRange,
    });
  }, [dateRange]);

  const options = useMemo(() => {
    return [
      {
        label: "",
        value: "custom",
        renderOption: ({ isSelected }) => (
          <DateRangeCalendarSelect
            calendarClassName="-ml-1 mt-0.5"
            value={dateRangeInterval}
            maxLookbackDays={
              timeGranularity === "hourly" ? maxLookbackDays : undefined
            }
            onApply={setDateRange}
            buttonComponent={(ref, refProps) => (
              <div
                ref={ref}
                {...refProps}
                className={clsx("h-full flex items-center cursor-pointer", {
                  "text-white": isSelected,
                })}
              >
                Custom
              </div>
            )}
          />
        ),
      },
      ...dateRangeOptionsForTimeGranularity(timeGranularity).map((e) => {
        return {
          label: DATE_RANGE_CHOICE_LABELS[e.value],
          value: e.value,
          onClick: () => {
            prettyLogEventPage({
              domain: "Top Level Filters",
              action: "onChange",
              additionalActionInfo: "Set Date Range",
              data: {
                newDateRange: e.value,
                pageContext,
              },
            });

            setDateRange(e.value);
          },
        };
      }),
    ] as FlatSelectOption<string>[];
  }, [
    dateRangeInterval,
    maxLookbackDays,
    prettyLogEventPage,
    setDateRange,
    timeGranularity,
  ]);

  return (
    <FlatSelect
      className={className}
      options={options}
      setSelected={setSelected}
      selected={selected}
    />
  );
};
